// src/components/SiteVisitTracker.jsx
// Counts visits on the PUBLIC pages (home, pricing, free trial…) for the admin "Visitors" tab.
// Dashboards are not counted. Nothing personal is stored in the browser: only a random id.
//
// Put <SiteVisitTracker /> inside <Router> in App.js, and call trackVisitEvent('demo_request')
// after the free trial form is sent.
// Logged-in students / teachers are marked (the login token is sent, the server checks it).
// Admin / sales: this browser is never counted (also after logging out).
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const API = `${process.env.REACT_APP_API_BASE_URL}/api/visit`;
const PRIVATE_RE = /^\/(admin|teacher|student|sales|studentprofile|democlass)(\/|$)/i;
const SESSION_IDLE_MS = 30 * 60 * 1000; // a new visit after 30 min without activity
const PING_MS = 30 * 1000;

const store = {
  get: (s, k) => {
    try {
      return window[s].getItem(k);
    } catch {
      return null;
    }
  },
  set: (s, k, v) => {
    try {
      window[s].setItem(k, v);
    } catch {
      /* private mode */
    }
  },
};

const newId = () =>
  (window.crypto && window.crypto.randomUUID
    ? window.crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
  ).slice(0, 40);

const visitorId = () => {
  let id = store.get('localStorage', 'rm-vid');
  if (!id) {
    id = newId();
    store.set('localStorage', 'rm-vid', id);
  }
  return id;
};

// same visit while the tab is used; a new one after 30 min of no activity
const session = () => {
  const now = Date.now();
  let id = store.get('sessionStorage', 'rm-sid');
  const last = Number(store.get('sessionStorage', 'rm-slast') || 0);
  let isNew = false;
  if (!id || now - last > SESSION_IDLE_MS) {
    id = newId();
    isNew = true;
    store.set('sessionStorage', 'rm-sid', id);
    store.set('sessionStorage', 'rm-landing-search', window.location.search || '');
  }
  store.set('sessionStorage', 'rm-slast', String(now));
  return { id, isNew };
};

// the login token (if any) and its role, read without checking (the server checks it)
const loginToken = () => {
  const raw = store.get('localStorage', 'userData');
  if (!raw) return '';
  try {
    const t = JSON.parse(raw);
    return typeof t === 'string' ? t : '';
  } catch {
    return raw;
  }
};
const roleOf = (token) => {
  try {
    const part = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return Number(JSON.parse(atob(part)).role) || 0;
  } catch {
    return 0;
  }
};
const STAFF_RE = /^\/(admin|sales)(\/|$)/i;
const isStaffBrowser = (pathname) => {
  if (store.get('localStorage', 'rm-staff') === '1') return true;
  const role = roleOf(loginToken());
  if (role === 1 || role === 4 || STAFF_RE.test(pathname)) {
    store.set('localStorage', 'rm-staff', '1'); // remember: this is the academy's own browser
    return true;
  }
  return false;
};

const send = (path, body) => {
  try {
    const token = loginToken();
    fetch(`${API}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* stats must never break the site */
  }
};

export const trackVisitEvent = (type) => {
  const sid = store.get('sessionStorage', 'rm-sid');
  if (sid) send('/event', { sessionId: sid, type });
};

const SiteVisitTracker = () => {
  const location = useLocation();
  const isPrivate = PRIVATE_RE.test(location.pathname) || isStaffBrowser(location.pathname);

  // one page view each time the page changes
  useEffect(() => {
    if (isPrivate || !process.env.REACT_APP_API_BASE_URL) return undefined;
    // wait a moment so the page title is set
    const t = setTimeout(() => {
      const s = session();
      send('', {
        visitorId: visitorId(),
        sessionId: s.id,
        path: location.pathname,
        title: document.title,
        referrer: document.referrer || '',
        search: s.isNew ? window.location.search : store.get('sessionStorage', 'rm-landing-search') || '',
        lang: navigator.language || '',
        host: window.location.hostname,
        timezone: (() => {
          try {
            return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
          } catch {
            return '';
          }
        })(),
      });
    }, 400);
    return () => clearTimeout(t);
  }, [location.pathname, isPrivate]);

  // Someone else on the same browser (log out, then a brother / another teacher logs in):
  // start a new visit, so each person gets their own visit and name.
  useEffect(() => {
    const tail = (loginToken() || '').slice(-12);
    const prev = store.get('sessionStorage', 'rm-who');
    if (prev !== null && prev !== tail && prev !== '') {
      ['rm-sid', 'rm-slast', 'rm-identified'].forEach((k) => {
        try {
          window.sessionStorage.removeItem(k);
        } catch {
          /* private mode */
        }
      });
    }
    store.set('sessionStorage', 'rm-who', tail);
  }, [location.pathname]);

  // logged in during this visit (home -> login -> dashboard): tell the server once, so the visit gets the name.
  // Runs on every page, dashboards included. For an admin the server removes the visit.
  useEffect(() => {
    if (!process.env.REACT_APP_API_BASE_URL) return;
    const token = loginToken();
    if (!token) return;
    // no visit yet in this tab (browser was closed and opened again, or straight to the dashboard):
    // start one, the server creates it with the person's name
    const sid = store.get('sessionStorage', 'rm-sid') || session().id;
    const mark = `${sid}:${token.slice(-12)}`;
    if (store.get('sessionStorage', 'rm-identified') === mark) return;
    store.set('sessionStorage', 'rm-identified', mark);
    send('/event', {
      sessionId: sid,
      type: 'login',
      visitorId: visitorId(),
      timezone: (() => {
        try {
          return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
        } catch {
          return '';
        }
      })(),
    });
  }, [location.pathname]);

  // "still here" pings for time on site: at 5s and 15s, then every 30s while the tab is visible,
  // and once more when the visitor leaves (closes the tab, switches app, goes back).
  // Before, only the 30s ping existed, so anyone who left within 30s showed "0s".
  useEffect(() => {
    if (isPrivate || !process.env.REACT_APP_API_BASE_URL) return undefined;
    const ping = (onlyVisible = true) => {
      if (onlyVisible && document.visibilityState !== 'visible') return;
      const sid = store.get('sessionStorage', 'rm-sid');
      if (!sid) return;
      store.set('sessionStorage', 'rm-slast', String(Date.now()));
      send('/ping', { sessionId: sid });
    };
    const early = [setTimeout(ping, 5000), setTimeout(ping, 15000)];
    const id = setInterval(ping, PING_MS);
    const onHide = () => {
      if (document.visibilityState === 'hidden') ping(false);
    };
    const onPageHide = () => ping(false);
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      early.forEach(clearTimeout);
      clearInterval(id);
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [isPrivate]);

  return null;
};

export default SiteVisitTracker;
